import React from 'react';
import { trustContent, clientLogos, testimonials } from '../data/mock';
import { Users, Building2, BookOpen, AlertCircle } from 'lucide-react';

const iconMap = {
  Users,
  Building2,
  BookOpen,
  AlertCircle,
};

// Silhouette avatar components - each with unique personality
const MaleAvatar1 = () => (
  <svg viewBox="0 0 40 40" className="w-12 h-12">
    <circle cx="20" cy="20" r="18" fill="#E2E8F0"/>
    <circle cx="20" cy="15" r="6" fill="#94A3B8"/>
    <ellipse cx="20" cy="32" rx="10" ry="8" fill="#94A3B8"/>
  </svg>
);

const MaleAvatar2 = () => (
  <svg viewBox="0 0 40 40" className="w-12 h-12">
    <circle cx="20" cy="20" r="18" fill="#E2E8F0"/>
    <circle cx="20" cy="14" r="6" fill="#94A3B8"/>
    <path d="M10 38 Q10 26 20 24 Q30 26 30 38" fill="#94A3B8"/>
    <path d="M12 10 Q12 5 20 5 Q28 5 28 10 L28 14 Q24 12 20 12 Q16 12 12 14 Z" fill="#94A3B8"/>
  </svg>
);

const FemaleAvatar1 = () => (
  <svg viewBox="0 0 40 40" className="w-12 h-12">
    <circle cx="20" cy="20" r="18" fill="#E2E8F0"/>
    <circle cx="20" cy="14" r="6" fill="#94A3B8"/>
    <ellipse cx="20" cy="33" rx="9" ry="7" fill="#94A3B8"/>
    <path d="M12 11 Q12 4 20 4 Q28 4 28 11 Q28 16 26 18 L24 13 Q20 15 16 13 L14 18 Q12 16 12 11" fill="#94A3B8"/>
  </svg>
);

const FemaleAvatar2 = () => (
  <svg viewBox="0 0 40 40" className="w-12 h-12">
    <circle cx="20" cy="20" r="18" fill="#E2E8F0"/>
    <circle cx="20" cy="15" r="6" fill="#94A3B8"/>
    <ellipse cx="20" cy="33" rx="9" ry="7" fill="#94A3B8"/>
    <path d="M13 9 Q13 3 20 3 Q27 3 27 9 L27 15 Q24 13 20 13 Q16 13 13 15 Z" fill="#94A3B8"/>
  </svg>
);

const avatarComponents = {
  'female-1': FemaleAvatar1,
  'female-2': FemaleAvatar2,
  'male-1': MaleAvatar1,
  'male-2': MaleAvatar2,
};

const TrustSection = () => {
  // Assign unique avatars to each testimonial
  const avatarAssignments = ['female-1', 'male-1', 'female-2', 'male-2'];

  return (
    <section className="py-20 lg:py-28 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            {trustContent.title}
          </h2>
          {trustContent.subtitle && (
            <p className="text-slate-400 text-lg max-w-2xl mx-auto whitespace-pre-line">
              {trustContent.subtitle}
            </p>
          )}
        </div>

        {/* Trust Points */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {trustContent.points.map((point, index) => {
            const IconComponent = iconMap[point.icon];
            const isWarning = point.icon === 'AlertCircle';
            return (
              <div
                key={index}
                className={`relative p-6 rounded-2xl border transition-all duration-300 ${
                  isWarning
                    ? 'bg-slate-800/30 border-amber-500/30'
                    : 'bg-slate-800/50 border-slate-700/50 hover:border-teal-500/30'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
                    isWarning ? 'bg-amber-500/10' : 'bg-teal-500/10'
                  }`}>
                    {IconComponent && (
                      <IconComponent className={`w-6 h-6 ${isWarning ? 'text-amber-400' : 'text-teal-400'}`} />
                    )}
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-2">
                      {point.title}
                    </h3>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      {point.linkText && point.linkUrl ? (
                        <>
                          {point.description.split(point.linkText)[0]}
                          <a 
                            href={point.linkUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-cyan-400 hover:text-cyan-300 transition-colors"
                          >
                            {point.linkText}
                          </a>
                          {point.description.split(point.linkText)[1]}
                        </>
                      ) : (
                        point.description
                      )}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Client Logos - White Background */}
        <div className="bg-white rounded-2xl py-12 px-8 mb-16">
          <p className="text-center text-slate-600 text-sm mb-8 font-medium">
            600社以上が登録するミライトマッチが独自開発
          </p>
          
          {/* Logo Container - Responsive Grid */}
          <div className="grid grid-cols-2 md:flex md:flex-wrap justify-center items-center gap-6 md:gap-12">
            {clientLogos.map((logo, index) => (
              <div
                key={index}
                className="flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
              >
                <img
                  src={logo.url}
                  alt={logo.name}
                  style={{ width: logo.width, height: 'auto' }}
                  className="max-h-12 object-contain max-w-[120px] md:max-w-none"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials Section */}
        <div>
          <h3 className="text-xl font-semibold text-white text-center mb-8">
            利用者の声
          </h3>
          <div className="grid sm:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => {
              const AvatarComponent = avatarComponents[avatarAssignments[index]];
              return (
                <div
                  key={index}
                  className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6"
                >
                  <div className="flex items-start gap-4">
                    {/* Silhouette Avatar */}
                    <div className="flex-shrink-0">
                      <AvatarComponent />
                    </div>
                    <div>
                      <p className="text-slate-300 text-sm leading-relaxed mb-3">
                        「{testimonial.quote}」
                      </p>
                      <p className="text-slate-500 text-xs">
                        — {testimonial.author}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSection;